import { createTheme, responsiveFontSizes, alpha } from '@mui/material/styles';

// Sky Theme
const palette = {
  mode: 'light',
  common: {
    black: '#000000',
    white: '#FFFFFF',
  },
  primary: {
    light: '#E3F2FD',
    main: '#64B5F6',
    dark: '#1E88E5',
    text: '#64B5F6',
    contrastText: '#FFFFFF',
  },
  secondary: {
    light: '#B3E5FC',
    main: '#4FC3F7',
    dark: '#039BE5',
    contrastText: '#FFFFFF',
  },
  success: {
    light: '#E8F5E9',
    main: '#66BB6A',
    dark: '#43A047',
    contrastText: '#FFFFFF',
  },
  warning: {
    light: '#FFF3E0',
    main: '#FFB74D',
    dark: '#F57C00',
    contrastText: '#FFFFFF',
  },
  error: {
    light: '#FFEBEE',
    main: '#EF5350',
    dark: '#D32F2F',
    contrastText: '#FFFFFF',
  },
  info: {
    light: '#E1F5FE',
    main: '#29B6F6',
    dark: '#0288D1',
    contrastText: '#FFFFFF',
  },
  neutral: {
    light: '#E3F2FD',
    main: '#90CAF9',
    dark: '#64B5F6',
    contrastText: '#FFFFFF',
  },
  appointment: {
    main: '#66BB6A',
  },
  patient: {
    main: '#64B5F6',
  },
  alert: {
    main: '#EF5350',
  },
  background: {
    default: '#F5FCFF',
    paper: '#FFFFFF',
    subtle: '#E3F2FD',
  },
  divider: 'rgba(100, 181, 246, 0.12)',
  text: {
    primary: '#0D47A1',
    secondary: '#1565C0',
    disabled: '#90CAF9',
  },
};

// Reuse the same typography configuration
const typography = {
  fontFamily: "'Satoshi', sans-serif",
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,
  h1: { 
    fontSize: '2.5rem', 
    fontWeight: 700, 
    lineHeight: 1.2,
    letterSpacing: '-0.01em',
  },
  h2: { 
    fontSize: '2rem', 
    fontWeight: 700, 
    lineHeight: 1.2,
    letterSpacing: '-0.01em',
  },
  h3: { 
    fontSize: '1.5rem', 
    fontWeight: 600, 
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
  },
  h4: { 
    fontSize: '1.25rem', 
    fontWeight: 600, 
    lineHeight: 1.4
  },
  h5: { 
    fontSize: '1.125rem', 
    fontWeight: 600, 
    lineHeight: 1.5
  },
  h6: { 
    fontSize: '1rem', 
    fontWeight: 600, 
    lineHeight: 1.5
  },
  subtitle1: { 
    fontSize: '1rem', 
    fontWeight: 500,
    lineHeight: 1.5,
    color: palette.text.secondary,
  },
  subtitle2: { 
    fontSize: '0.875rem', 
    fontWeight: 500,
    lineHeight: 1.57,
    color: palette.text.secondary,
  },
  body1: { 
    fontSize: '1rem', 
    lineHeight: 1.6,
    letterSpacing: '0.01em',
  },
  body2: { 
    fontSize: '0.875rem', 
    fontWeight: 300,
    lineHeight: 1.57,
    letterSpacing: '0.01em',
  },
  button: { 
    fontSize: '0.875rem', 
    fontWeight: 600, 
    letterSpacing: '0.02em',
    textTransform: 'none',
  },
  caption: { 
    fontSize: '0.75rem', 
    lineHeight: 1.5,
    color: palette.text.secondary,
  },
  overline: {
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.06em',
    lineHeight: 1.6,
    textTransform: 'uppercase',
  },
};

const shape = { borderRadius: 12 };
const spacing = 8;

const components = {
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        textTransform: 'none',
        fontWeight: 600,
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
        },
      },
      contained: {
        boxShadow: 'none',
        background: `linear-gradient(135deg, ${alpha(palette.primary.main, 1)} , ${alpha(palette.primary.main, 1)} )`,
        backdropFilter: 'blur(10px)', 
        borderRadius: '12px',
        WebkitBackdropFilter: 'blur(10px)',
        color: palette.common.white,
        border: `1px solid ${alpha(palette.common.white, 0.2)}`,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${alpha(palette.common.white, 0.2)} 0%, transparent 100%)`,
          borderRadius: 'inherit',
          pointerEvents: 'none'
        },
        '&:hover': {
          boxShadow: 'none',
          background: `linear-gradient(135deg, ${alpha(palette.primary.main, 1)} 20%, ${alpha(palette.secondary.main, 1)} 100%)`,
          transform: 'translateY(-1px)',
          transition: 'all 0.2s ease-in-out'
        },
      },
      outlined: {
        borderWidth: '1.5px',
        borderColor: alpha(palette.primary.main, 0.3),
        background: `linear-gradient(135deg, ${alpha(palette.common.white, 0.1)} 0%, ${alpha(palette.common.white, 0.05)} 100%)`,
        backdropFilter: 'blur(5px)',
        WebkitBackdropFilter: 'blur(5px)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${alpha(palette.common.white, 0.1)} 0%, transparent 100%)`,
          borderRadius: 'inherit',
          pointerEvents: 'none'
        },
        '&:hover': {
          borderWidth: '1.5px',
          borderColor: alpha(palette.primary.main, 0.5),
          background: `linear-gradient(135deg, ${alpha(palette.common.white, 0.15)} 0%, ${alpha(palette.common.white, 0.1)} 100%)`,
          transform: 'translateY(-1px)',
          transition: 'all 0.2s ease-in-out'
        },
      },
      text: {
        '&:hover': {
          background: alpha(palette.primary.main, 0.04),
        },
      },
      sizeSmall: {
        padding: '4px 12px',
        fontSize: '0.8125rem',
      },
      sizeMedium: {
        padding: '6px 16px',
        fontSize: '0.875rem',
      },
      sizeLarge: {
        padding: '8px 20px',
        fontSize: '0.9375rem',
      },
    },
    defaultProps: {
      disableElevation: true,
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 3,
        background: `linear-gradient(135deg, ${alpha(palette.primary.main, 0.1)} 20%, ${alpha(palette.secondary.light, 0.1)} 100%)`,
        backdropFilter: 'blur(5px)',
        border: `1px solid ${alpha(palette.common.white, 0.1)}`,
        overflow: 'hidden',
        transition: 'all 0.3s ease-in-out',
        borderRadius: `24px 24px 24px 24px`,
        WebkitBackdropFilter: 'blur(20px)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${alpha(palette.common.white, 0.1)} 0%, transparent 100%)`,
          borderRadius: 'inherit',
          pointerEvents: 'none'
        },
        '&:hover': {
          transform: 'translateY(-2px)',
          background: `linear-gradient(145deg, ${alpha(palette.primary.light, 0.1)} 20%, ${alpha(palette.secondary.light, 0.1)} 100%)`,
        },
      }
    }
  },
  MuiCardHeader: {
    styleOverrides: {
      root: {
        padding: '24px',
        position: 'relative',
        zIndex: 1
      },
      title: {
        fontSize: '1.25rem',
        fontWeight: 600,
      },
      subheader: {
        fontSize: '0.875rem',
        color: alpha(palette.text.secondary, 0.9),
      }
    }
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: '24px',
        position: 'relative',
        zIndex: 1,
        '&:last-child': {
          paddingBottom: '24px',
        }
      }
    }
  },
  MuiCardActions: {
    styleOverrides: {
      root: {
        padding: '16px 24px',
        justifyContent: 'flex-end',
        position: 'relative',
        zIndex: 1
      }
    }
  }
};

let theme = createTheme({ palette, typography, shape, spacing, components });
theme = responsiveFontSizes(theme);

export default theme; 